import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Skeleton,
  Divider,
  IconButton,
  Snackbar,
  Fab
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import TopBar from './TopBar';
import { format, subDays, startOfDay, endOfDay, startOfMonth, endOfMonth, eachMonthOfInterval, getMonth, getYear, subYears } from 'date-fns';
import { ko } from 'date-fns/locale';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { 
  getFollowingUserIds, 
  fetchTimelineByDate, 
  fetchTimelineByDateRange 
} from '../services/timelineService';

// 월간 타임라인 데이터 가져오기 함수
const fetchMonthlyTimelineData = async (startMonth, endMonth, followingUserIds, currentUserId) => {
  try {
    const monthlyData = {};
    
    // 각 월별로 데이터 수집
    const months = eachMonthOfInterval({ start: startMonth, end: endMonth });
    
    for (const month of months) {
      const monthKey = format(month, 'yyyy-MM');
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      
      // 해당 월의 모든 사용자 책 데이터 가져오기
      // Firebase 인덱스 오류를 방지하기 위해 단순화된 쿼리 사용
      const booksQuery = query(
        collection(db, 'books'),
        where('uid', 'in', [currentUserId, ...followingUserIds]),
        orderBy('startedAt', 'desc')
      );
      
      const booksSnapshot = await getDocs(booksQuery);
      const monthBooks = [];
      
      for (const bookDoc of booksSnapshot.docs) {
        const bookData = bookDoc.data();
        
        // 클라이언트 사이드에서 날짜 필터링
        if (!bookData.startedAt) continue;
        
        const startDate = bookData.startedAt.toDate ? bookData.startedAt.toDate() : new Date(bookData.startedAt);
        const startMonth = format(startDate, 'yyyy-MM');
        
        // 해당 월에 속하지 않으면 건너뛰기
        if (startMonth !== monthKey) continue;
        
        // 사용자 정보 가져오기
        let userDisplayName = '알 수 없는 사용자';
        let userPhotoURL = '';
        let isCurrentUser = false;
        
        if (bookData.uid === currentUserId) {
          const currentUser = auth.currentUser;
          userDisplayName = currentUser?.displayName || '나';
          userPhotoURL = currentUser?.photoURL || '';
          isCurrentUser = true;
        } else {
          try {
            const userDoc = await getDoc(doc(db, 'users', bookData.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              userDisplayName = userData.displayName || '알 수 없는 사용자';
              userPhotoURL = userData.photoURL || '';
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }
        
        // 다중 읽은 기간 처리
        if (bookData.readingPeriods && Array.isArray(bookData.readingPeriods)) {
          // 해당 월에 시작일이 있는 모든 기간을 개별 항목으로 추가
          bookData.readingPeriods.forEach((period, index) => {
            if (period.startDate) {
              const periodStartDate = period.startDate.toDate ? period.startDate.toDate() : new Date(period.startDate);
              const periodStartMonth = format(periodStartDate, 'yyyy-MM');
              
              if (periodStartMonth === monthKey) {
                monthBooks.push({
                  bookId: bookDoc.id,
                  bookTitle: bookData.title || '제목 없음',
                  bookThumbnail: bookData.thumbnail || bookData.userThumbnail || '/fallback-image.png',
                  bookAuthors: bookData.authors || [],
                  status: bookData.readStatus || 'not_started',
                  startedAt: periodStartDate,
                  userId: bookData.uid,
                  userDisplayName: userDisplayName,
                  userPhotoURL: userPhotoURL,
                  isCurrentUser: isCurrentUser,
                  periodIndex: index // 기간 인덱스 추가
                });
              }
            }
          });
        } else {
          // 기존 단일 기간 데이터 처리
          monthBooks.push({
            bookId: bookDoc.id,
            bookTitle: bookData.title || '제목 없음',
            bookThumbnail: bookData.thumbnail || bookData.userThumbnail || '/fallback-image.png',
            bookAuthors: bookData.authors || [],
            status: bookData.readStatus || 'not_started',
            startedAt: startDate,
            userId: bookData.uid,
            userDisplayName: userDisplayName,
            userPhotoURL: userPhotoURL,
            isCurrentUser: isCurrentUser,
            periodIndex: 0
          });
        }
      }
      
      if (monthBooks.length > 0) {
        monthlyData[monthKey] = monthBooks;
      }
    }
    
    return monthlyData;
  } catch (error) {
    console.error('Error fetching monthly timeline data:', error);
    throw error;
  }
};

// 상수 정의
const DAYS_PER_LOAD = 7;
const SHOW_EMPTY_DAYS = false;
const MAX_PAST_DAYS = 100; // 최대 100일까지 과거 데이터 로드
const MONTHS_PER_LOAD = 6; // 월간 뷰에서 한 번에 로드할 월 수

// 한국 시간 기준 오늘 날짜 가져오기
const getKoreanToday = () => {
  const now = new Date();
  const koreanTime = new Date(now.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
  return format(koreanTime, 'yyyy-MM-dd');
};

// 요일별 색상 매핑
const getDayColor = (dayOfWeek, isToday = false) => {
  if (isToday) return '#1976d2'; // 오늘은 파란색
  switch (dayOfWeek) {
    case '토': return '#1976d2'; // 토요일 파란색
    case '일': return '#d32f2f'; // 일요일 빨간색
    default: return '#333'; // 평일 검은색
  }
};

// 캘린더 형태의 타임라인 컴포넌트
const TimelineCalendar = ({ timelineData, onItemClick, onUserClick, onLoadMorePast, isLoadingMore, dateRange, scrollContainerRef, isAdjustingScroll, lastScrollPosition }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const initialScrollSet = useRef(false);
  
  // 날짜 범위 계산 (동적으로 계산)
  const { startDate, endDate } = dateRange;
  
  // 날짜 배열 생성 (과거부터 최신 순으로) - 메모이제이션
  const dates = useMemo(() => {
    const dateArray = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dateArray.push(new Date(currentDate));
      currentDate = subDays(currentDate, -1);
    }
    return dateArray;
  }, [startDate, endDate]);

  // 날짜 헤더 데이터 메모이제이션
  const dateHeaders = useMemo(() => {
    return dates.map((date, index) => {
      const dateKey = format(date, 'yyyy-MM-dd');
      const month = format(date, 'M', { locale: ko });
      const day = format(date, 'd', { locale: ko });
      const isToday = dateKey === getKoreanToday();
      const dayOfWeek = format(date, 'E', { locale: ko });
      const dayColor = getDayColor(dayOfWeek, isToday);
      
      // 월이 바뀌는 지점인지 확인 (첫 번째 날짜이거나 이전 날짜와 월이 다른 경우)
      const isMonthChange = index === 0 || 
        (index > 0 && format(dates[index - 1], 'M', { locale: ko }) !== month);
      
      // 1일인지 확인 (월 표시용)
      const isFirstDayOfMonth = day === '1';
      
      return {
        dateKey,
        month,
        day,
        isToday,
        dayColor,
        dayOfWeek, // 색상 계산용으로만 유지
        isMonthChange,
        isFirstDayOfMonth
      };
    });
  }, [dates]);

  // 가로 스크롤 핸들러 (무한 스크롤)
  const handleScroll = useCallback((e) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target;
    
    // 스크롤 위치 저장 (자동 조정 중이 아닐 때만)
    if (!isAdjustingScroll.current) {
      lastScrollPosition.current = scrollLeft;
    }
    
    // 왼쪽 끝에 도달했을 때 (50px 여유로 감소하여 더 정확한 감지)
    if (scrollLeft < 50 && !isLoadingMore && onLoadMorePast) {
      console.log('Triggering load more past days...', { 
        scrollLeft, 
        scrollWidth, 
        clientWidth,
        maxScrollLeft: scrollWidth - clientWidth 
      });
      onLoadMorePast();
    }
  }, [isLoadingMore, onLoadMorePast]);

  // 사용자별 높이 계산 함수 - 개선된 책 카드 디자인에 맞춤
  const calculateUserRowHeight = useCallback((userId) => {
    let maxBooksInRow = 0;
    Object.values(timelineData).forEach(items => {
      const userItems = items.filter(item => item.userId === userId);
      maxBooksInRow = Math.max(maxBooksInRow, userItems.length);
    });
    
    // 최대 3개로 제한
    maxBooksInRow = Math.min(maxBooksInRow, 3);
    
    // 개선된 책 카드 높이 계산
    // 책 썸네일(2:3 비율, 80px × 120px) + 패딩(8px)
    // 각 책 아이템: 120px(썸네일 높이) + 8px(패딩) = 128px
    // 컨테이너 패딩: 8px (p: 0.5 = 4px * 2)
    const bookItemHeight = 128; // 썸네일 높이 + 패딩
    const containerPadding = 8; // p: 0.5 = 4px * 2
    
    if (maxBooksInRow === 0) {
      return 120; // 기본 높이
    } else {
      return maxBooksInRow * bookItemHeight + containerPadding;
    }
  }, [timelineData]);

  // 사용자 목록 (나 + 팔로우한 사람들) - 빈 행 제거
  const usersWithData = useMemo(() => {
    const userMap = new Map();
    
    Object.values(timelineData).forEach(items => {
      items.forEach(item => {
        const userKey = item.userId;
        if (!userMap.has(userKey)) {
          userMap.set(userKey, {
            userId: item.userId,
            userDisplayName: item.userDisplayName,
            userPhotoURL: item.userPhotoURL,
            isCurrentUser: item.isCurrentUser
          });
        }
      });
    });
    
    // 우선순위 정렬: 현재 사용자 > 팔로우한 사용자
    return Array.from(userMap.values()).sort((a, b) => {
      if (a.isCurrentUser && !b.isCurrentUser) return -1;
      if (!a.isCurrentUser && b.isCurrentUser) return 1;
      return 0;
    });
  }, [timelineData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return { bgcolor: '#FFD700', color: '#000' };
      case 'reading':
        return { bgcolor: '#4facfe', color: '#fff' };
      case 'not_started':
        return { bgcolor: '#f5f5f5', color: '#888' };
      default:
        return { bgcolor: '#f5f5f5', color: '#888' };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return '완독';
      case 'reading':
        return '읽는 중';
      case 'not_started':
        return '읽기 전';
      default:
        return '읽기 전';
    }
  };

  // 초기 렌더링 시 스크롤 위치 설정 (무한 스크롤 중에는 비활성화)
  useEffect(() => {
    // 무한 스크롤로 인한 데이터 변경이 아닌 경우에만 초기 스크롤 설정
    if (!initialScrollSet.current && dates.length > 0 && !isLoadingMore) {
      const setScrollToToday = () => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && scrollContainer.scrollWidth > 0 && !isAdjustingScroll.current) {
          scrollContainer.scrollLeft = scrollContainer.scrollWidth;
          console.log('TimelineCalendar: Initial scroll position set to today');
          initialScrollSet.current = true;
        }
      };

      // 즉시 시도
      setScrollToToday();
      
      // DOM 렌더링 완료를 보장하기 위해 여러 번 시도
      setTimeout(setScrollToToday, 50);
      setTimeout(setScrollToToday, 100);
      setTimeout(setScrollToToday, 200);
      setTimeout(setScrollToToday, 300);
    }
  }, [dates.length, isLoadingMore]);

  return (
    <Box sx={{ position: 'relative', mt: 0 }}>
      {/* 가로 스크롤 로딩 인디케이터 */}
      {isLoadingMore && (
        <Box
          sx={{
            position: 'absolute',
            left: 60,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: '50%',
            p: 1.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.1)'
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      
      <Box sx={{ display: 'flex' }}>
                 {/* 고정된 사용자 정보 영역 */}
         <Box sx={{ 
           width: 40, 
           flexShrink: 0,
           bgcolor: '#fafafa',
           borderRight: '1px solid #eee'
         }}>
                       {/* 날짜 헤더 공간 - 우측 날짜 헤더와 높이 일치 */}
            <Box sx={{ 
              borderBottom: '2px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1.5,
              minHeight: '45px' // 우측 날짜 헤더와 동일한 높이
            }} />
          
                     {/* 사용자별 고정 영역 */}
           {usersWithData.map((user) => {
             const rowHeight = calculateUserRowHeight(user.userId);
             
             return (
                                                <Box
                  key={user.userId}
                  sx={{
                    height: rowHeight, // 정확한 높이 설정
                    minHeight: rowHeight, // 최소 높이 설정
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5, // 우측 책 칸과 동일한 패딩 (4px)
                    borderBottom: '1px solid #eee',
                    bgcolor: user.isCurrentUser ? '#f0f8ff' : '#fafafa'
                  }}
                >
                 <Typography
                   variant="body2"
                   sx={{
                     fontWeight: user.isCurrentUser ? 600 : 500,
                     cursor: 'pointer',
                     '&:hover': { textDecoration: 'underline' },
                     overflow: 'hidden',
                     textOverflow: 'ellipsis',
                     display: '-webkit-box',
                     WebkitLineClamp: 3,
                     WebkitBoxOrient: 'vertical',
                     fontSize: '0.7rem',
                     color: user.isCurrentUser ? '#1976d2' : '#333',
                     textAlign: 'center',
                     lineHeight: 1.1,
                     wordBreak: 'break-word'
                   }}
                   onClick={() => onUserClick(user.userId)}
                 >
                   {user.userDisplayName}
                 </Typography>
               </Box>
             );
           })}
        </Box>

                           {/* 스크롤 가능한 날짜 영역 */}
          <Box 
            ref={scrollContainerRef}
            sx={{ 
              flex: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
            onScroll={handleScroll}
          >
                       <Box sx={{ 
              minWidth: Math.max(600, dates.length * 100), // 2:3 비율 카드에 맞게 조정
              p: 0
            }}>
                                                   {/* 날짜 헤더 */}
              <Box sx={{ display: 'flex', mb: 0, pt: 0, pr: 2, pb: 0, pl: 0 }}>
               {dateHeaders.map((header) => (
                 <Box
                   key={header.dateKey}
                   sx={{
                     flex: 1,
                     minWidth: 100, // 2:3 비율 카드에 맞게 조정
                     textAlign: 'center',
                     p: 0,
                     borderBottom: '2px solid #eee',
                     fontWeight: 600,
                     fontSize: '0.9rem',
                     display: 'flex',
                     flexDirection: 'column',
                     justifyContent: 'center',
                                           minHeight: '45px', // 높이 축소
                     position: 'relative',
                     ...(header.isToday && {
                       bgcolor: 'rgba(25, 118, 210, 0.08)',
                       borderBottom: '2px solid #1976d2',
                       '&::after': {
                         content: '""',
                         position: 'absolute',
                         top: 0,
                         left: 0,
                         right: 0,
                         height: '3px',
                         bgcolor: '#1976d2',
                         borderRadius: '0 0 2px 2px'
                       }
                     }),
                     ...(header.isMonthChange && {
                       borderLeft: '2px solid #e0e0e0',
                       bgcolor: 'rgba(0, 0, 0, 0.02)'
                     })
                   }}
                 >
                                                            <Box 
                       sx={{ 
                         fontSize: '0.9rem',
                         fontWeight: header.isToday ? 700 : 600,
                         color: header.dayColor,
                         lineHeight: 1,
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         height: '100%',
                         position: 'relative',
                         mt: 0.5,
                         pb: 0.3,
                         gap: 0.5
                       }}
                     >
                       {/* 월 표시 chip - 1일인 경우에만 표시 */}
                       {header.isFirstDayOfMonth && (
                         <Box
                           sx={{
                             fontSize: '0.6rem',
                             fontWeight: 600,
                             color: '#666',
                             bgcolor: '#f0f0f0',
                             px: 0.8,
                             py: 0.2,
                             borderRadius: 0.5,
                             border: '1px solid #d0d0d0',
                             whiteSpace: 'nowrap',
                             lineHeight: 1,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             minWidth: 'fit-content'
                           }}
                         >
                           {header.month}월
                         </Box>
                       )}
                       <span>{header.day}</span>
                     </Box>
                 </Box>
               ))}
             </Box>

                         {/* 사용자별 행 */}
             {usersWithData.map((user) => {
               const rowHeight = calculateUserRowHeight(user.userId);
               
                               return (
                  <Box 
                    key={user.userId} 
                    sx={{ 
                      display: 'flex', 
                      mb: 1, 
                      p: 0, 
                      pl: 0,
                      height: rowHeight, // 부모 컨테이너에 정확한 높이 설정
                      minHeight: rowHeight // 최소 높이도 동일하게 설정
                    }}
                  >
                   {/* 날짜별 책 정보 */}
                   {dates.map((date) => {
                     const dateKey = format(date, 'yyyy-MM-dd');
                     const dayItems = timelineData[dateKey]?.filter(item => 
                       item.userId === user.userId
                     ) || [];

                     return (
                                               <Box
                          key={dateKey}
                          sx={{
                            flex: 1,
                            minWidth: 100, // 2:3 비율 카드에 맞게 조정 (80px + 여유공간)
                            p: 0.5, // 좌측 닉네임 칸과 동일한 패딩 (4px)
                            borderRight: '1px solid #eee',
                            height: rowHeight, // 정확한 높이 설정
                            minHeight: rowHeight, // 최소 높이 설정
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            alignItems: 'center' // 카드를 중앙 정렬
                          }}
                        >
                                                   {dayItems.slice(0, 3).map((item, index) => (
                            <Box
                              key={`${item.bookId}_${index}`}
                              sx={{
                                p: 0, // 가로 패딩 제거
                                borderRadius: 1,
                                cursor: 'pointer',
                                fontSize: '0.7rem',
                                ...getStatusColor(item.status),
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.02)'
                                },
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '120px', // 책 썸네일 높이와 동일
                                width: '80px' // 책 썸네일 너비와 동일 (2:3 비율)
                              }}
                              onClick={() => onItemClick(item)}
                              title={`${item.bookTitle} - ${getStatusText(item.status)}`}
                            >
                              {/* 책 썸네일 - 2:3 비율로 개선 */}
                              <Box
                                component="img"
                                src={item.bookThumbnail || '/fallback-image.png'}
                                alt={`${item.bookTitle}${item.authors ? ` - ${item.authors.join(', ')}` : ''}`}
                                loading="lazy"
                                sx={{
                                  width: '100%', // 카드 너비에 맞춤
                                  height: '100%', // 카드 높이에 맞춤
                                  objectFit: 'cover',
                                  borderRadius: 0.5,
                                  border: '1px solid #eee',
                                  boxShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                                  bgcolor: '#f5f5f5'
                                }}
                                onError={(e) => {
                                  e.target.src = '/fallback-image.png';
                                }}
                              />
                            </Box>
                          ))}
                       </Box>
                     );
                   })}
                 </Box>
               );
             })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// 월간 그래프 뷰 컴포넌트 (개인 독서 통계)
const MonthlyGraphView = ({ monthlyData, currentUserId }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // 현재 사용자의 월별 독서 데이터만 필터링
  const myMonthlyData = useMemo(() => {
    const filteredData = {};
    Object.entries(monthlyData).forEach(([monthKey, books]) => {
      const myBooks = books.filter(book => book.userId === currentUserId);
      if (myBooks.length > 0) {
        filteredData[monthKey] = myBooks;
      }
    });
    return filteredData;
  }, [monthlyData, currentUserId]);
  
  // 월별 책 개수 계산
  const monthlyBookCounts = useMemo(() => {
    const counts = {};
    for (let month = 1; month <= 12; month++) {
      const monthKey = `${selectedYear}-${String(month).padStart(2, '0')}`;
      counts[month] = myMonthlyData[monthKey]?.length || 0;
    }
    return counts;
  }, [myMonthlyData, selectedYear]);
  
  // 최대 책 개수 (차트 높이 계산용)
  const maxBooks = useMemo(() => {
    return Math.max(...Object.values(monthlyBookCounts), 1);
  }, [monthlyBookCounts]);
  
  return (
    <Box sx={{ 
      pt: 1, 
      bgcolor: '#fff', 
      minHeight: 'calc(100vh - 80px - 56px)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* 연도 선택기 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: 3,
        mb: 3
      }}>
        <IconButton 
          onClick={() => setSelectedYear(prev => prev - 1)}
          sx={{ 
            color: '#999',
            width: 32,
            height: 32,
            '&:hover': {
              color: '#666',
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          minWidth: 80, 
          textAlign: 'center',
          color: '#333',
          fontSize: '1.5rem'
        }}>
          {selectedYear}
        </Typography>
        <IconButton 
          onClick={() => setSelectedYear(prev => prev + 1)}
          sx={{ 
            color: '#999',
            width: 32,
            height: 32,
            '&:hover': {
              color: '#666',
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <ChevronRightIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
      </Box>
      {/* 차트 영역 */}
      <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          justifyContent: 'space-between',
          height: 400, // 높이 대폭 증가 (모든 썸네일 표시)
          gap: 1, // 간격 축소
          px: 0, // 좌우 패딩 축소
          width: '100%', // 화면 너비에 맞춤
          flex: 1 // 남은 공간 차지
        }}>
          {Array.from({ length: 12 }, (_, monthIndex) => {
            const month = monthIndex + 1;
            const bookCount = monthlyBookCounts[month];
            const heightPercentage = (bookCount / maxBooks) * 100;
            
            return (
              <Box key={month} sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                flex: 1,
                position: 'relative'
              }}>

                
                {/* 바 차트 */}
                <Box sx={{
                  width: '100%',
                  height: '100%'
                }}>
                  {/* 책 썸네일 스택 (책이 있는 경우) */}
                  {bookCount > 0 && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1, // 간격 조정 (공간 효율성)
                      mb: 1, // 하단 여백 축소
                      maxHeight: 350, // 최대 높이 대폭 증가 (모든 썸네일 표시)
                      overflow: 'visible', // 오버플로우 허용 (모든 썸네일 표시)
                      width: '45px', // 너비 축소 (더 많은 책 표시)
                      zIndex: 5 // 겹침 순서 조정
                    }}>
                      {myMonthlyData[`${selectedYear}-${String(month).padStart(2, '0')}`]
                        ?.map((book, index) => ( // 모든 책 표시 (개수 제한 제거)
                          <Box
                            key={`${book.bookId}_${book.periodIndex}`}
                            component="img"
                            src={book.bookThumbnail}
                            alt={`${book.bookTitle}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                            loading="lazy"
                            sx={{
                              width: '45px', // 너비 축소 (더 많은 책 표시)
                              height: '68px', // 2:3 비율 유지
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '2px solid #fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              bgcolor: '#f5f5f5',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                zIndex: 10
                              }
                            }}
                            onError={(e) => {
                              e.target.src = '/fallback-image.png';
                            }}
                          />
                        ))}
                    </Box>
                  )}
                </Box>
                
                {/* 월 표시 */}
                <Typography variant="caption" sx={{ 
                  mt: 1, 
                  fontWeight: 500,
                  color: bookCount > 0 ? '#333' : '#999',
                  fontSize: '0.7rem'
                }}>
                  {month}월
                </Typography>
              </Box>
            );
          })}
        </Box>
        
        {/* 통계 요약 */}
        <Box sx={{ 
          mt: 3, 
          pt: 2, 
          borderTop: '1px solid #eee',
          textAlign: 'center',
          pb: 2
        }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            총 {Object.values(monthlyBookCounts).reduce((sum, count) => sum + count, 0)}권 읽음
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
            가장 많이 읽은 달: {
              Object.entries(monthlyBookCounts).reduce((max, [month, count]) => 
                count > max.count ? { month, count } : max, 
                { month: 0, count: 0 }
              ).month || 0
            }월
          </Typography>
        </Box>
      {/* 월별 독서 통계 차트 */}
      
    </Box>
  );
};

// 월간 뷰 컴포넌트
const MonthlyView = ({ monthlyData, onItemClick, onUserClick, onLoadMorePast, isLoadingMore, dateRange }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // 데이터가 있는 연도 목록 추출 (최신순)
  const availableYears = useMemo(() => {
    const years = new Set();
    Object.keys(monthlyData).forEach(monthKey => {
      const year = parseInt(monthKey.split('-')[0], 10);
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a); // 최신순 정렬
  }, [monthlyData]);
  
  // 선택된 연도에 데이터가 없으면 자동으로 최신 데이터가 있는 연도로 이동
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]); // 최신 연도로 이동
    }
  }, [availableYears, selectedYear]);
  
  // 선택된 연도의 월별 데이터 정렬 (최신 월부터)
  const sortedMonthsForYear = useMemo(() => {
    return Object.entries(monthlyData)
      .filter(([monthKey]) => monthKey.startsWith(`${selectedYear}-`))
      .filter(([, books]) => books.length > 0)
      .sort(([a], [b]) => b.localeCompare(a)); // 최신 월부터 정렬
  }, [monthlyData, selectedYear]);
  
  // 사용자별로 그룹화
  const usersWithData = useMemo(() => {
    const userMap = new Map();
    
    Object.values(monthlyData).forEach(books => {
      books.forEach(book => {
        const userKey = book.userId;
        if (!userMap.has(userKey)) {
          userMap.set(userKey, {
            userId: book.userId,
            userDisplayName: book.userDisplayName,
            userPhotoURL: book.userPhotoURL,
            isCurrentUser: book.isCurrentUser
          });
        }
      });
    });
    
    // 우선순위 정렬: 현재 사용자 > 팔로우한 사용자
    return Array.from(userMap.values()).sort((a, b) => {
      if (a.isCurrentUser && !b.isCurrentUser) return -1;
      if (!a.isCurrentUser && b.isCurrentUser) return 1;
      return 0;
    });
  }, [monthlyData]);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return { bgcolor: '#FFD700', color: '#000' };
      case 'reading':
        return { bgcolor: '#4facfe', color: '#fff' };
      case 'not_started':
        return { bgcolor: '#f5f5f5', color: '#888' };
      default:
        return { bgcolor: '#f5f5f5', color: '#888' };
    }
  };
  
  return (
    <Box sx={{ p: 2, pt: 1 }}>
      {/* 연도 선택기 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        mb: 3,
        gap: 3
      }}>
        <IconButton 
          onClick={() => setSelectedYear(prev => prev - 1)}
          sx={{ 
            color: '#999',
            width: 32,
            height: 32,
            '&:hover': {
              color: '#666',
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          minWidth: 80, 
          textAlign: 'center',
          color: '#333',
          fontSize: '1.5rem'
        }}>
          {selectedYear}
        </Typography>
        <IconButton 
          onClick={() => setSelectedYear(prev => prev + 1)}
          sx={{ 
            color: '#999',
            width: 32,
            height: 32,
            '&:hover': {
              color: '#666',
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <ChevronRightIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
      </Box>
      
      {/* 월별 책 스택 */}
      {availableYears.length === 0 ? (
        // 모든 연도에 데이터가 없는 경우
        <Box sx={{ 
          textAlign: 'center', 
          py: 8, 
          px: 3,
          maxWidth: 400,
          mx: 'auto'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            읽은 책이 없어요
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
            첫 번째 책을 등록하고 독서 여정을 시작해보세요.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sortedMonthsForYear.map(([monthKey, monthBooks]) => {
          // 월 번호 추출 (예: "2024-03" -> 3)
          const monthNumber = parseInt(monthKey.split('-')[1], 10);
          
          // 사용자별로 그룹화
          const booksByUser = {};
          monthBooks.forEach(book => {
            if (!booksByUser[book.userId]) {
              booksByUser[book.userId] = [];
            }
            booksByUser[book.userId].push(book);
          });
          
          return (
            <Box key={monthKey} sx={{ mb: 2 }}>
              {/* 월 헤더 */}
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                {monthNumber}월
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 400 }}>
                  ({monthBooks.length}권)
                </Typography>
              </Typography>
              
              {/* 사용자별 책 스택 */}
              {usersWithData.map(user => {
                const userBooks = booksByUser[user.userId] || [];
                if (userBooks.length === 0) return null;
                
                return (
                  <Box key={user.userId} sx={{ mb: 2 }}>
                    {/* 사용자 정보 */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => onUserClick(user.userId)}
                    >
                      <Typography variant="body2" sx={{ 
                        fontWeight: user.isCurrentUser ? 600 : 500,
                        color: user.isCurrentUser ? '#1976d2' : '#333',
                        fontSize: '0.9rem'
                      }}>
                        {user.userDisplayName}
                      </Typography>
                    </Box>
                    
                    {/* 책 스택 */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1,
                      pl: 0
                    }}>
                      {userBooks.map((book, index) => (
                        <Box
                          key={`${book.bookId}_${book.periodIndex}`}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              zIndex: 10
                            },
                            transition: 'all 0.2s ease',
                            ...getStatusColor(book.status)
                          }}
                          onClick={() => onItemClick(book)}
                          title={`${book.bookTitle} - ${book.userDisplayName}`}
                        >
                          <Box
                            component="img"
                            src={book.bookThumbnail}
                            alt={`${book.bookTitle}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                            loading="lazy"
                            sx={{
                              width: 60,
                              height: 90,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '2px solid #fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              bgcolor: '#f5f5f5'
                            }}
                            onError={(e) => {
                              e.target.src = '/fallback-image.png';
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}
        </Box>
      )}
      
      {/* 더 로드하기 버튼 */}
      {isLoadingMore && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

// 빈 상태 컴포넌트
const EmptyState = ({ type, onSearchClick, onShareClick }) => {
  if (type === 'no-following') {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 8, 
        px: 3,
        maxWidth: 400,
        mx: 'auto'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          당신의 독서 타임라인, 지금 시작해 보세요
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
          친구를 팔로우하면 그들의 읽는 책이 하루 단위로 업데이트돼요. 함께 읽는 듯한 몰입감을 느껴보세요.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={onSearchClick}
            sx={{ minWidth: 120 }}
          >
            사람 찾기
          </Button>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={onShareClick}
            sx={{ minWidth: 120 }}
          >
            친구 초대
          </Button>
        </Box>
      </Box>
    );
  }

  if (type === 'no-activity') {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 8, 
        px: 3,
        maxWidth: 400,
        mx: 'auto'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          최근 일주일 간 새 활동이 없어요
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
          조금만 기다리면 새로운 기록이 쌓일 거예요. 그 사이, 관심 있는 사람을 더 팔로우해 보세요.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onSearchClick}
        >
          사람 찾기
        </Button>
      </Box>
    );
  }

  return null;
};

function TimelinePage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [followingUserIds, setFollowingUserIds] = useState([]);
  const [timelineData, setTimelineData] = useState({});
  const [monthlyData, setMonthlyData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('monthly'); // 'daily' | 'monthly' | 'monthly-graph'
  
  const [rangeStartDate, setRangeStartDate] = useState(new Date());
  const [rangeEndDate, setRangeEndDate] = useState(new Date());
  const [monthlyRangeStart, setMonthlyRangeStart] = useState(new Date());
  const [monthlyRangeEnd, setMonthlyRangeEnd] = useState(new Date());
  
  const loadingRef = useRef(false);
  const pastDaysLoaded = useRef(0);
  const scrollPositionRef = useRef(0);
  const lastScrollPosition = useRef(0);
  const isAdjustingScroll = useRef(false);
  const scrollContainerRef = useRef(null);

  // 초기 데이터 로딩
  const loadInitialData = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    pastDaysLoaded.current = 0;
    loadingRef.current = false;
    setHasMorePast(true);

    try {
      // 팔로우 중인 사용자 ID 가져오기
      const followingIds = await getFollowingUserIds(currentUser.uid);
      setFollowingUserIds(followingIds);

      // 일간 뷰 데이터
      const endDate = new Date();
      const startDate = subDays(endDate, DAYS_PER_LOAD - 1);
      
      const timelineData = await fetchTimelineByDateRange(startDate, endDate, followingIds, SHOW_EMPTY_DAYS, currentUser.uid);
      setTimelineData(timelineData);
      setRangeStartDate(startDate);
      setRangeEndDate(endDate);

      // 월간 뷰 데이터는 별도로 로딩 (오류 발생 시에도 일간 뷰는 정상 작동)
      // 최근 2년 데이터를 로드하여 올해 데이터가 없어도 작년 데이터를 표시
      try {
        const currentYear = new Date().getFullYear();
        const monthlyStart = subYears(new Date(currentYear, 0, 1), 1); // 작년 1월 1일
        const monthlyEnd = new Date(currentYear, 11, 31); // 올해 12월 31일
        
        const monthlyTimelineData = await fetchMonthlyTimelineData(monthlyStart, monthlyEnd, followingIds, currentUser.uid);
        setMonthlyData(monthlyTimelineData);
        setMonthlyRangeStart(monthlyStart);
        setMonthlyRangeEnd(monthlyEnd);
      } catch (monthlyError) {
        console.error('Error loading monthly data:', monthlyError);
        // 월간 데이터 로딩 실패 시에도 일간 뷰는 정상 작동하도록 설정
        setMonthlyData({});
      }

    } catch (error) {
      console.error('Error loading initial timeline data:', error);
      setError('타임라인을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 과거 데이터 더 로딩 (무한 스크롤)
  const loadMorePastDays = useCallback(async () => {
    if (loadingRef.current || !currentUser || pastDaysLoaded.current >= MAX_PAST_DAYS) {
      console.log('Load more blocked:', { 
        loading: loadingRef.current, 
        hasMore: hasMorePast, 
        user: !!currentUser, 
        pastDays: pastDaysLoaded.current 
      });
      return;
    }

    console.log('Loading more past days...');
    loadingRef.current = true;
    setLoadingMore(true);

    try {
      // 현재 시작 날짜보다 이전 7일을 로드
      const newStartDate = subDays(rangeStartDate, DAYS_PER_LOAD);
      const newEndDate = subDays(rangeStartDate, 1);
      
      console.log('Loading date range:', { 
        newStartDate: newStartDate.toISOString(), 
        newEndDate: newEndDate.toISOString() 
      });
      
      const newTimelineData = await fetchTimelineByDateRange(
        newStartDate, 
        newEndDate, 
        followingUserIds,
        SHOW_EMPTY_DAYS,
        currentUser.uid
      );

      console.log('New timeline data loaded:', Object.keys(newTimelineData));

      // 연속으로 빈 데이터가 3번 이상 나오면 더 이상 로드하지 않음
      const nonEmptyDays = Object.values(newTimelineData).filter(items => items.length > 0).length;
      if (nonEmptyDays === 0) {
        // 빈 데이터 카운터 증가
        const emptyDataCount = (loadMorePastDays.emptyDataCount || 0) + 1;
        loadMorePastDays.emptyDataCount = emptyDataCount;
        
        if (emptyDataCount >= 3) {
          console.log('No data found for 3 consecutive loads, stopping load more');
          setHasMorePast(false);
          return;
        }
      } else {
        // 데이터가 있으면 카운터 리셋
        loadMorePastDays.emptyDataCount = 0;
      }

             // 스크롤 위치 보존을 위해 현재 스크롤 위치와 컨테이너 정보 저장
       const scrollContainer = scrollContainerRef.current;
       const currentScrollLeft = lastScrollPosition.current || scrollContainer?.scrollLeft || 0;
       const currentScrollWidth = scrollContainer?.scrollWidth || 0;
       const currentClientWidth = scrollContainer?.clientWidth || 0;

       console.log('Before data update - Scroll info:', {
         currentScrollLeft,
         currentScrollWidth,
         currentClientWidth,
         maxScrollLeft: currentScrollWidth - currentClientWidth,
         pastDaysLoaded: pastDaysLoaded.current
       });

       // 현재 스크롤 위치에서 보이는 컨텐츠의 상대적 위치를 계산
       const relativeScrollPosition = currentScrollWidth > 0 ? currentScrollLeft / currentScrollWidth : 0;

      // 새로운 데이터를 기존 데이터 앞에 추가 (날짜 순서 유지)
      setTimelineData(prev => {
        const mergedData = { ...newTimelineData, ...prev };
        console.log('Merged timeline data:', Object.keys(mergedData));
        return mergedData;
      });
      
      setRangeStartDate(newStartDate);
      pastDaysLoaded.current += DAYS_PER_LOAD;

             // React 상태 업데이트 후 DOM 렌더링 완료를 기다린 후 스크롤 위치 조정
       const adjustScrollPosition = (newEndDate) => {
         if (scrollContainer && !isAdjustingScroll.current) {
           isAdjustingScroll.current = true;
           
           const newScrollWidth = scrollContainer.scrollWidth;
           const widthDifference = newScrollWidth - currentScrollWidth;
           
           console.log('After data update - Scroll info:', {
             newScrollWidth,
             widthDifference,
             currentScrollLeft,
             relativeScrollPosition,
             maxScrollLeft: newScrollWidth - currentClientWidth
           });
           
           // 새로 로딩된 데이터의 마지막 일자(newEndDate)가 화면에 보이도록 스크롤 위치 계산
           // newEndDate는 새로 로딩된 데이터 중 가장 최근 일자 (예: 14일)
           
           // 새로 로딩된 데이터의 마지막 일자 위치를 계산
           // 전체 날짜 배열에서 newEndDate의 인덱스를 찾아서 해당 위치로 스크롤
           const newEndDateKey = format(newEndDate, 'yyyy-MM-dd');
           
           // 날짜별 컬럼 너비 (140px)와 패딩을 고려한 계산
           const columnWidth = 140; // 각 날짜 컬럼의 너비
           const totalColumns = Math.ceil(newScrollWidth / columnWidth);
           
           // newEndDate가 전체 날짜 배열에서 몇 번째 컬럼인지 계산
           // 과거부터 최신 순으로 정렬된 날짜 배열에서 newEndDate의 위치
           const sortedDates = Object.keys({ ...newTimelineData, ...timelineData })
             .sort((a, b) => new Date(a) - new Date(b));
           
           const newEndDateIndex = sortedDates.indexOf(newEndDateKey);
           
           if (newEndDateIndex !== -1) {
             // newEndDate 컬럼이 화면 중앙에 오도록 스크롤 위치 계산
             const targetColumnPosition = newEndDateIndex * columnWidth;
             const screenCenter = currentClientWidth / 2;
             const targetScrollLeft = Math.max(0, targetColumnPosition - screenCenter + columnWidth / 2);
             const maxScrollLeft = newScrollWidth - currentClientWidth;
             
             // 스크롤 위치가 최대값을 초과하지 않도록 보장
             const safeScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));
             
             // 스크롤 위치 설정
             scrollContainer.scrollLeft = safeScrollLeft;
             
             console.log('Scroll position adjusted to new end date:', {
               newEndDate: newEndDateKey,
               newEndDateIndex,
               targetColumnPosition,
               screenCenter,
               targetScrollLeft,
               safeScrollLeft,
               actual: scrollContainer.scrollLeft,
               maxScrollLeft
             });
             
             // 스크롤 위치 업데이트
             lastScrollPosition.current = safeScrollLeft;
           } else {
             console.warn('New end date not found in sorted dates:', newEndDateKey);
           }
           
           // 조정 완료 후 플래그 해제 (더 오래 유지하여 초기 스크롤 설정 방지)
           setTimeout(() => {
             isAdjustingScroll.current = false;
           }, 1000);
         }
       };

             // React 상태 업데이트 후 DOM 렌더링 완료를 기다림
       setTimeout(() => adjustScrollPosition(newEndDate), 0);
       setTimeout(() => adjustScrollPosition(newEndDate), 50);
       setTimeout(() => adjustScrollPosition(newEndDate), 100);
       setTimeout(() => adjustScrollPosition(newEndDate), 200);
       setTimeout(() => adjustScrollPosition(newEndDate), 300);
       setTimeout(() => adjustScrollPosition(newEndDate), 500);
       setTimeout(() => adjustScrollPosition(newEndDate), 800);

    } catch (error) {
      console.error('Error loading more past days:', error);
      setSnackbar({ 
        open: true, 
        message: '과거 데이터를 불러오는 중 오류가 발생했습니다.', 
        severity: 'error' 
      });
    } finally {
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [rangeStartDate, followingUserIds, hasMorePast, currentUser, pastDaysLoaded]);

  // 아이템 클릭 핸들러
  const handleItemClick = useCallback((item) => {
    navigate(`/book/${item.bookId}`);
  }, [navigate]);

  // 사용자 클릭 핸들러
  const handleUserClick = useCallback((userId) => {
    navigate(`/u/${userId}`);
  }, [navigate]);

  // 검색 화면으로 이동
  const handleSearchClick = useCallback(() => {
    navigate('/search');
  }, [navigate]);

  // 뷰 모드 전환 핸들러
  const handleViewModeChange = useCallback((event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  }, []);

  // 공유 기능
  const handleShareClick = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: '스냅로그 - 독서 타임라인',
        text: '친구들과 함께 독서 타임라인을 공유해보세요!',
        url: window.location.origin
      });
    } else {
      // 폴백: 클립보드에 복사
      navigator.clipboard.writeText(window.location.origin);
      setSnackbar({ 
        open: true, 
        message: '링크가 클립보드에 복사되었습니다.', 
        severity: 'success' 
      });
    }
  }, []);

  // 인증 상태 변경 감지
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // 사용자 변경 시 데이터 로딩
  useEffect(() => {
    if (currentUser) {
      loadInitialData();
    }
  }, [currentUser, loadInitialData]);

  // 초기 데이터 로딩 완료 후 스크롤 위치 설정 (무한 스크롤 중에는 비활성화)
  useEffect(() => {
    // 무한 스크롤로 인한 데이터 변경이 아닌 경우에만 초기 스크롤 설정
    if (!loading && Object.keys(timelineData).length > 0 && !loadingMore && pastDaysLoaded.current === 0) {
      // DOM 렌더링 완료 후 스크롤 위치 설정
      const setScrollToToday = () => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && scrollContainer.scrollWidth > 0 && !isAdjustingScroll.current) {
          scrollContainer.scrollLeft = scrollContainer.scrollWidth;
          console.log('Initial scroll position set to today (right end)');
        }
      };

      // 즉시 시도
      setScrollToToday();
      
      // DOM 렌더링 완료를 보장하기 위해 여러 번 시도
      setTimeout(setScrollToToday, 50);
      setTimeout(setScrollToToday, 100);
      setTimeout(setScrollToToday, 150);
      setTimeout(setScrollToToday, 300);
      setTimeout(setScrollToToday, 500);
    }
  }, [loading, timelineData, loadingMore]);

  // 타임라인 데이터를 날짜별로 정렬하고 빈 날짜 제거 (과거부터 최신 순으로)
  const sortedTimelineData = useMemo(() => {
    return Object.entries(timelineData)
      .sort(([a], [b]) => new Date(a) - new Date(b)) // 과거부터 최신 순으로 정렬
      .filter(([, items]) => items.length > 0)
      .reduce((acc, [date, items]) => {
        acc[date] = items;
        return acc;
      }, {});
  }, [timelineData]);

  // 빈 상태 확인
  const hasNoFollowing = followingUserIds.length === 0;
  const hasNoActivity = !loading && Object.keys(sortedTimelineData).length === 0;

  return (
    <>
      {/* <TopBar title="타임라인" showBack={false} /> */}
      
      <Box sx={{ pb: 7, width: '100%', bgcolor: '#fff', minHeight: 'calc(100vh - 80px - 56px)' }}>
        {/* 상단 전용 영역 - 뷰 전환 버튼용 */}
        <Box sx={{ 
          height: 60, // 상단 영역 높이 축소
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
          borderBottom: '1px solid #eee',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          {/* 트렌디한 뷰 모드 전환 버튼 */}
          <Box sx={{
            display: 'flex',
            bgcolor: '#f5f5f5',
            borderRadius: '999px',
            p: 0.3,
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
            position: 'relative',
            minWidth: 180
          }}>
            {['daily', 'monthly', 'monthly-graph'].map((mode) => (
              <Box
                key={mode}
                onClick={() => handleViewModeChange(null, mode)}
                sx={{
                  flex: 1,
                  py: 0.8,
                  px: 1.5,
                  borderRadius: '999px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: viewMode === mode ? '#fff' : '#666',
                  bgcolor: viewMode === mode ? '#333' : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  zIndex: 1,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '32px',
                  '&:hover': {
                    color: viewMode === mode ? '#fff' : '#333',
                    bgcolor: viewMode === mode ? '#333' : 'rgba(0,0,0,0.05)'
                  },
                  '&:active': {
                    transform: 'scale(0.98)'
                  }
                }}
              >
                {mode === 'daily' && '일간'}
                {mode === 'monthly' && '월간'}
                {mode === 'monthly-graph' && '그래프'}
              </Box>
            ))}
          </Box>
        </Box>

        {loading ? (
          // 로딩 스켈레톤
          <Box sx={{ p: 2, pt: 1 }} data-testid="timeline-loading">
                         {viewMode === 'daily' ? (
               // 일간 뷰 로딩 스켈레톤
               <>
                 <Box sx={{ display: 'flex', mb: 2 }}>
                   <Box sx={{ width: 40, flexShrink: 0 }} />
                   {[...Array(7)].map((_, index) => (
                     <Box key={index} sx={{ flex: 1, minWidth: 100, textAlign: 'center', p: 0.5, position: 'relative', minHeight: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                       <Skeleton variant="text" width="15%" height={14} />
                       <Skeleton variant="text" width="10%" height={16} />
                     </Box>
                   ))}
                 </Box>
                 {[...Array(3)].map((_, userIndex) => (
                   <Box key={userIndex} sx={{ display: 'flex', mb: 1 }}>
                     <Box sx={{ width: 40, flexShrink: 0, p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                       <Skeleton variant="text" width="90%" height={16} />
                     </Box>
                     {[...Array(7)].map((_, dateIndex) => (
                       <Box key={dateIndex} sx={{ flex: 1, minWidth: 100, p: 0.5 }}>
                         <Skeleton variant="rectangular" width="100%" height={120} />
                       </Box>
                     ))}
                   </Box>
                 ))}
               </>
             ) : viewMode === 'monthly' ? (
               // 월간 뷰 로딩 스켈레톤
               <Box sx={{ pt: 4 }}>
                 {[...Array(6)].map((_, index) => (
                   <Box key={index} sx={{ mb: 3 }}>
                     <Skeleton variant="text" width="20%" height={24} sx={{ mb: 2 }} />
                     <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                       {[...Array(8)].map((_, bookIndex) => (
                         <Skeleton key={bookIndex} variant="rectangular" width={60} height={90} sx={{ borderRadius: 1 }} />
                       ))}
                     </Box>
                   </Box>
                 ))}
               </Box>
             ) : (
               // 그래프 뷰 로딩 스켈레톤
               <Box sx={{ pt: 4 }}>
                 <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                   <Skeleton variant="text" width={80} height={32} />
                 </Box>
                 <Box sx={{ 
                   bgcolor: '#fff', 
                   borderRadius: 2, 
                   p: 3, 
                   boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                   border: '1px solid #eee'
                 }}>
                   <Skeleton variant="text" width="60%" height={28} sx={{ mb: 3, mx: 'auto' }} />
                   <Box sx={{ 
                     display: 'flex', 
                     alignItems: 'flex-end', 
                     justifyContent: 'space-between',
                     height: 200,
                     gap: 1,
                     px: 2
                   }}>
                     {[...Array(12)].map((_, index) => (
                       <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                         <Skeleton variant="rectangular" width="100%" height={Math.random() * 150 + 20} sx={{ borderRadius: '4px 4px 0 0' }} />
                         <Skeleton variant="text" width="80%" height={16} sx={{ mt: 1 }} />
                       </Box>
                     ))}
                   </Box>
                 </Box>
               </Box>
             )}
          </Box>
        ) : error ? (
          // 에러 상태
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={loadInitialData}>
              다시 시도
            </Button>
          </Box>
        ) : hasNoFollowing ? (
          // 팔로우 중인 사용자가 없는 경우
          <EmptyState 
            type="no-following" 
            onSearchClick={handleSearchClick}
            onShareClick={handleShareClick}
          />
        ) : viewMode === 'daily' ? (
          // 일간 뷰
          hasNoActivity ? (
            // 활동이 없는 경우
            <EmptyState 
              type="no-activity" 
              onSearchClick={handleSearchClick}
            />
          ) : (
            <TimelineCalendar
              timelineData={sortedTimelineData}
              onItemClick={handleItemClick}
              onUserClick={handleUserClick}
              onLoadMorePast={loadMorePastDays}
              isLoadingMore={loadingMore}
              dateRange={{ startDate: rangeStartDate, endDate: rangeEndDate }}
              scrollContainerRef={scrollContainerRef}
              isAdjustingScroll={isAdjustingScroll}
              lastScrollPosition={lastScrollPosition}
            />
          )
        ) : viewMode === 'monthly-graph' ? (
          // 월간 그래프 뷰 (개인 독서 통계)
          <MonthlyGraphView
            monthlyData={monthlyData}
            currentUserId={currentUser?.uid}
          />
        ) : (
          // 월간 뷰 (데이터가 없으면 MonthlyView 컴포넌트 내부에서 처리)
          <MonthlyView
            monthlyData={monthlyData}
            onItemClick={handleItemClick}
            onUserClick={handleUserClick}
            onLoadMorePast={loadMorePastDays}
            isLoadingMore={loadingMore}
            dateRange={{ startDate: monthlyRangeStart, endDate: monthlyRangeEnd }}
          />
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TimelinePage;
